const COORD_PATTERNS = [
  /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/, // the actual pin location on a "place" link — checked first, most precise
  /[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/, // ?q=lat,lng
  /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/, // @lat,lng,zoom — just the map's viewport center, can be off if the user panned
];

/** Extracts lat/lng straight out of a full Google Maps URL. Shortened links (maps.app.goo.gl/…)
 * never carry coordinates in the URL itself — resolve those via /api/geo/resolve-maps-link first. */
export function parseLatLngFromMapsUrl(url: string): { lat: string; lng: string } | null {
  for (const pattern of COORD_PATTERNS) {
    const match = url.match(pattern);
    if (match) return { lat: match[1]!, lng: match[2]! };
  }
  return null;
}

export function isShortGoogleMapsLink(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === 'maps.app.goo.gl' || host === 'goo.gl';
  } catch {
    return false;
  }
}

export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Location is not supported on this device')); return; }
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 });
  });
}

/** Beyond this the OS is handing the browser an "approximate" (coarse, km-grid) location rather than
 * a GPS fix — real fixes are 5–50 m. No branch geofence can be matched reliably at that accuracy. */
export const APPROXIMATE_ACCURACY_M = 500;

export type LocationPermission = 'granted' | 'prompt' | 'denied' | 'unknown';

/** Safari < 16 has no Permissions API for geolocation, so 'unknown' means "just ask". */
export async function queryLocationPermission(): Promise<LocationPermission> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' });
    return status?.state ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/** A website can ask for location but can't ask for *precise* location — that's the person's choice
 * in the OS/browser prompt, and once they pick Approximate (or Block) only settings can undo it. So
 * the best the app can do is say exactly where that setting lives on their phone. */
export function locationSettingsSteps(problem: 'approximate' | 'denied'): string[] {
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const browser = /CriOS|Chrome/.test(ua) ? 'Chrome' : /Firefox|FxiOS/.test(ua) ? 'Firefox' : /SamsungBrowser/.test(ua) ? 'Samsung Internet' : 'Safari';
  if (isIos) {
    const app = browser === 'Safari' ? 'Safari Websites' : browser;
    return [
      `Open Settings → Privacy & Security → Location Services and make sure it is on.`,
      `Tap ${app} → choose "While Using the App".`,
      `Turn ON "Precise Location".`,
      'Come back here and tap "Check again".',
    ];
  }
  if (isAndroid) {
    return [
      'Make sure Location is ON in the quick settings (swipe down from the top).',
      `Open Settings → Apps → ${browser} → Permissions → Location → "Allow only while using the app".`,
      `Turn ON "Use precise location".`,
      ...(problem === 'denied' ? ['In this browser, tap the icon left of the web address → Permissions → Location → Allow.'] : []),
      'Come back here and tap "Check again".',
    ];
  }
  return [
    problem === 'denied' ? 'Click the icon left of the web address and allow Location for this site.' : "Turn on your device's location services.",
    'Reload the page and tap "Check again".',
  ];
}

export function geolocationErrorMessage(err: unknown): string {
  if (err instanceof GeolocationPositionError) {
    if (err.code === err.PERMISSION_DENIED) return 'Location permission denied — enable it in your browser settings.';
    if (err.code === err.POSITION_UNAVAILABLE) return 'Could not determine your location — try again outdoors or near a window.';
    if (err.code === err.TIMEOUT) return 'Location request timed out — try again.';
  }
  if (err instanceof Error) return err.message;
  return 'Could not get your location';
}
